/**
 * OnizlemeModal — Anket veya Checklist'in son kullanıcı görünümünü simüle eder
 * Props:
 *   tip: 'anket' | 'checklist'
 *   sablon: { baslik, aciklama, sorular: [] }
 *   onClose: fn
 */
import { useState } from 'react';
import { X, Star, CheckSquare, ChevronRight } from 'lucide-react';

const EMOJILER   = ['😡','😞','😐','😊','😍'];
const EMOJI_LBLS = ['Çok Kötü','Kötü','Orta','İyi','Mükemmel'];

function YildizSecici({ value, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      {[1,2,3,4,5].map(n => (
        <span key={n}
          onMouseEnter={() => setHover(n)}
          onMouseLeave={() => setHover(0)}
          onClick={() => onChange(n)}
          style={{ fontSize: 30, cursor: 'pointer', transition: 'transform 0.1s',
            transform: hover >= n || value >= n ? 'scale(1.15)' : 'scale(1)',
            filter: hover >= n || value >= n ? 'none' : 'grayscale(1) opacity(0.4)' }}>
          ⭐
        </span>
      ))}
    </div>
  );
}

function EmojiSecici({ value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
      {EMOJILER.map((em, i) => (
        <button key={i} type="button" onClick={() => onChange(i+1)}
          style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
            padding: '10px 14px', borderRadius: 12, border: `2px solid ${value===i+1?'#2563eb':'#e2e8f0'}`,
            background: value===i+1?'#eff6ff':'#fff', cursor: 'pointer', flex: 1, minWidth: 52,
            transition: 'all 0.12s'
          }}>
          <span style={{ fontSize: 28, transform: value===i+1?'scale(1.2)':'scale(1)', transition: 'transform 0.12s' }}>{em}</span>
          <span style={{ fontSize: 10, color: value===i+1?'#2563eb':'#94a3b8', fontWeight: value===i+1?700:400 }}>{EMOJI_LBLS[i]}</span>
        </button>
      ))}
    </div>
  );
}

function AnketSoruPreview({ s, cevap, onCevap }) {
  return (
    <div style={{ padding: '16px 0', borderBottom: '1px solid #f1f5f9' }}>
      <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 12, color: '#1e293b' }}>
        {s.soru_metni}
        {s.zorunlu && <span style={{ color: '#ef4444', marginLeft: 4 }}>*</span>}
      </div>

      {s.soru_tipi === 'yildiz' && (
        <YildizSecici value={cevap?.yildiz_degeri || 0} onChange={v => onCevap('yildiz_degeri', v)} />
      )}
      {s.soru_tipi === 'emoji' && (
        <EmojiSecici value={cevap?.emoji_degeri || 0} onChange={v => onCevap('emoji_degeri', v)} />
      )}
      {s.soru_tipi === 'evet_hayir' && (
        <div style={{ display: 'flex', gap: 10 }}>
          {['Evet','Hayır'].map(opt => (
            <button key={opt} type="button" onClick={() => onCevap('evet_hayir', opt==='Evet')}
              style={{
                flex: 1, padding: '12px 0', borderRadius: 10, border: `2px solid`,
                borderColor: cevap?.evet_hayir===(opt==='Evet') ? (opt==='Evet'?'#16a34a':'#ef4444') : '#e2e8f0',
                background: cevap?.evet_hayir===(opt==='Evet') ? (opt==='Evet'?'#f0fdf4':'#fef2f2') : '#fff',
                color: cevap?.evet_hayir===(opt==='Evet') ? (opt==='Evet'?'#16a34a':'#ef4444') : '#64748b',
                fontWeight: 600, fontSize: 14, cursor: 'pointer', transition: 'all 0.12s'
              }}>
              {opt === 'Evet' ? '✓ Evet' : '✗ Hayır'}
            </button>
          ))}
        </div>
      )}
      {s.soru_tipi === 'cok_secenekli' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {(s.secenekler || []).length === 0 && (
            <p style={{ color: '#94a3b8', fontSize: 13, fontStyle: 'italic' }}>Seçenek tanımlanmamış</p>
          )}
          {(s.secenekler || []).map((opt, i) => {
            const sec = cevap?.secili_secenek === opt;
            return (
              <label key={i} style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
                borderRadius: 10, border: `2px solid ${sec?'#2563eb':'#e2e8f0'}`,
                background: sec?'#eff6ff':'#fff', cursor: 'pointer', transition: 'all 0.12s'
              }}>
                <input type="radio" name={`prev_${s.id||s.soru_metni}`} checked={sec}
                  onChange={() => onCevap('secili_secenek', opt)}
                  style={{ width: 18, height: 18, accentColor: '#2563eb' }}/>
                <span style={{ fontSize: 14, fontWeight: sec?600:400, color: sec?'#2563eb':'#374151' }}>{opt}</span>
              </label>
            );
          })}
        </div>
      )}
      {s.soru_tipi === 'acik_metin' && (
        <textarea rows={2} placeholder="Cevabınızı yazın..." value={cevap?.acik_metin||''}
          onChange={e => onCevap('acik_metin', e.target.value)}
          style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1.5px solid #e2e8f0',
            fontSize: 14, resize: 'none', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}/>
      )}
    </div>
  );
}

function ChecklistSoruPreview({ s, cevap, onCevap, idx }) {
  return (
    <div style={{ padding: '14px 0', borderBottom: '1px solid #f1f5f9' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: s.soru_tipi!=='evet_hayir'?10:0 }}>
        <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#f1f5f9',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          fontSize: 11, fontWeight: 700, color: '#94a3b8', marginTop: 1 }}>{idx+1}</div>
        <div style={{ fontSize: 14, fontWeight: 500, flex: 1, color: '#1e293b' }}>
          {s.soru_metni}
          {s.zorunlu && <span style={{ color: '#ef4444', marginLeft: 4 }}>*</span>}
        </div>
      </div>

      <div style={{ paddingLeft: 36 }}>
        {s.soru_tipi === 'evet_hayir' && (
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            {['Evet','Hayır'].map(opt => (
              <button key={opt} type="button" onClick={() => onCevap('evet_hayir', opt==='Evet')}
                style={{
                  padding: '8px 20px', borderRadius: 8, border: `1.5px solid`,
                  borderColor: cevap?.evet_hayir===(opt==='Evet') ? (opt==='Evet'?'#16a34a':'#ef4444') : '#e2e8f0',
                  background: cevap?.evet_hayir===(opt==='Evet') ? (opt==='Evet'?'#f0fdf4':'#fef2f2') : '#fff',
                  color: cevap?.evet_hayir===(opt==='Evet') ? (opt==='Evet'?'#16a34a':'#ef4444') : '#64748b',
                  fontWeight: 600, fontSize: 13, cursor: 'pointer', transition: 'all 0.1s'
                }}>
                {opt === 'Evet' ? '✓ Evet' : '✗ Hayır'}
              </button>
            ))}
          </div>
        )}
        {s.soru_tipi === 'cok_secenekli' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {(s.secenekler || []).map((opt, i) => {
              const sec = cevap?.secili_secenek === opt;
              return (
                <label key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px',
                  borderRadius: 8, border: `1.5px solid ${sec?'#2563eb':'#e2e8f0'}`,
                  background: sec?'#eff6ff':'#fff', cursor: 'pointer'
                }}>
                  <input type="radio" name={`cl_prev_${s.id||s.soru_metni}`} checked={sec}
                    onChange={() => onCevap('secili_secenek', opt)}
                    style={{ width: 16, height: 16, accentColor: '#2563eb' }}/>
                  <span style={{ fontSize: 13, fontWeight: sec?600:400, color: sec?'#2563eb':'#374151' }}>{opt}</span>
                </label>
              );
            })}
          </div>
        )}
        {s.soru_tipi === 'puan' && (
          <YildizSecici value={cevap?.yildiz_degeri||0} onChange={v => onCevap('yildiz_degeri', v)} />
        )}
        {s.soru_tipi === 'acik_metin' && (
          <textarea rows={2} placeholder="Notunuzu yazın..." value={cevap?.acik_metin||''}
            onChange={e => onCevap('acik_metin', e.target.value)}
            style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0',
              fontSize: 13, resize: 'none', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', marginTop: 6 }}/>
        )}
      </div>
    </div>
  );
}

export default function OnizlemeModal({ tip, sablon, onClose }) {
  const [cevaplar, setCevaplar] = useState({});
  const sorular = sablon.sorular || [];

  const setCevap = (soruKey, field, val) => {
    setCevaplar(prev => ({ ...prev, [soruKey]: { ...prev[soruKey], [field]: val } }));
  };

  return (
    <div className="modal-overlay" style={{ zIndex: 1100 }}>
      <div style={{
        background: '#fff', borderRadius: 20, width: '100%', maxWidth: 480,
        maxHeight: '90vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 25px 60px rgba(0,0,0,0.2)', overflow: 'hidden'
      }}>
        {/* Başlık */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
              📱 Önizleme — {tip === 'anket' ? 'Anket' : 'Checklist'}
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>
              {sablon.baslik || 'İsimsiz'}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, color: '#94a3b8' }}>
            <X size={18} />
          </button>
        </div>

        {/* Telefon çerçevesi */}
        <div style={{ flex: 1, overflowY: 'auto', background: '#f8fafc', padding: '0 16px 16px' }}>
          <div style={{
            background: '#fff', borderRadius: 16, margin: '16px 0',
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)', overflow: 'hidden'
          }}>
            {/* Form başlık */}
            <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid #f1f5f9' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: tip==='anket'?'#eff6ff':'#f0fdf4',
                  display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {tip === 'anket' ? '📋' : <CheckSquare size={18} color="#16a34a"/>}
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#1e293b' }}>{sablon.baslik || 'Başlık'}</div>
                  {sablon.aciklama && <div style={{ fontSize: 12, color: '#94a3b8' }}>{sablon.aciklama}</div>}
                </div>
              </div>
              <div style={{ fontSize: 12, color: '#94a3b8' }}>
                {sorular.length} {tip === 'anket' ? 'soru' : 'madde'}
              </div>
            </div>

            {/* Sorular */}
            <div style={{ padding: '0 20px' }}>
              {sorular.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8', fontSize: 13 }}>
                  Henüz soru eklenmemiş
                </div>
              ) : (
                sorular.map((s, i) => (
                  tip === 'anket'
                    ? <AnketSoruPreview key={i} s={s} idx={i}
                        cevap={cevaplar[s.id || i]}
                        onCevap={(f, v) => setCevap(s.id || i, f, v)} />
                    : <ChecklistSoruPreview key={i} s={s} idx={i}
                        cevap={cevaplar[s.id || i]}
                        onCevap={(f, v) => setCevap(s.id || i, f, v)} />
                ))
              )}
            </div>

            {/* Gönder butonu */}
            <div style={{ padding: '16px 20px' }}>
              <button style={{
                width: '100%', padding: '14px 0', borderRadius: 12, border: 'none',
                background: tip==='anket'?'#2563eb':'#16a34a', color: '#fff',
                fontWeight: 700, fontSize: 15, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8
              }}>
                {tip === 'anket' ? 'Anketi Gönder' : 'Kontrolü Kaydet'}
                <ChevronRight size={16} />
              </button>
            </div>
          </div>

          {/* Önizleme notu */}
          <div style={{ textAlign: 'center', fontSize: 11, color: '#94a3b8', padding: '0 0 8px' }}>
            Bu önizlemedir — gönderilen veriler kaydedilmez
          </div>
        </div>
      </div>
    </div>
  );
}
